import { Injectable, signal, TemplateRef } from '@angular/core';
import { Observable, take, tap } from 'rxjs';
import {
  DrawerComponent,
  DrawerConfig,
  DrawerResult,
} from '../models/drawer.types';
import { DrawerRef } from './drawer-ref';

/**
 * Internal state of the drawer
 */
interface DrawerState<T = unknown> {
  isOpen: boolean;
  component: DrawerComponent | null;
  drawerRef: DrawerRef<T> | null;
  config: DrawerConfig;
}

/**
 * Service to manage the global drawer in the layout
 */
@Injectable({
  providedIn: 'root',
})
export class DrawerService {
  /**
   * Internal state signal
   */
  private readonly state = signal<DrawerState>({
    isOpen: false,
    component: null,
    drawerRef: null,
    config: {},
  });

  /**
   * Public readonly signals
   */
  readonly isOpen = signal(false);
  readonly component = signal<DrawerComponent | TemplateRef<unknown>>(null);
  readonly drawerRef = signal<DrawerRef | null>(null);
  readonly config = signal<DrawerConfig>({});

  /**
   * Opens the drawer with a component
   *
   * @param component - Component to load in the drawer
   * @param config - Configuration options
   * @returns Observable that emits when the drawer is closed
   */
  open<T = unknown, R = unknown>(
    component: DrawerComponent<T> | TemplateRef<unknown>,
    config: DrawerConfig<T> = {}
  ): Observable<DrawerResult<R>> {
    // Close any existing drawer first
    if (this.isOpen()) {
      this.close();
    }

    // Create drawer ref with config data
    const drawerRef = new DrawerRef<T, R>(config.data);

    // Set default config values
    const finalConfig: DrawerConfig<T> = {
      width: '400px',
      disableClose: false,
      hasBackdrop: true,
      ...config,
    };

    // Update state
    this.isOpen.set(true);
    this.component.set(component);
    this.drawerRef.set(drawerRef);
    this.config.set(finalConfig);

    // Return observable that completes when drawer closes
    return drawerRef.afterClosed$.pipe(
      take(1),
      tap(() => this.close())
    );
  }

  /**
   * Closes the drawer
   */
  close(): void {
    this.isOpen.set(false);
    this.component.set(null);
    this.drawerRef.set(null);
    this.config.set({});
  }

  /**
   * Toggles the drawer open/closed state
   */
  toggle(): void {
    if (this.isOpen()) {
      this.close();
    }
  }
}
